import React from "react";

import queryString from "query-string";
import { connect } from "react-redux";

import { colorsPalettes } from "@similarweb/styles";

import { apiHelper } from "app/common/services/apiHelper";
import categoryService from "app/common/services/categoryService";
import { ECategoryType } from "app/common/services/categoryService.types";
import { chosenSites } from "app/common/services/chosenSites";
import { swSettings } from "app/common/services/swSettings";
import { MAX_DOMAINS_IN_CATEGORY } from "app/components/customCategoriesWizard/custom-categories-max-domains";
import { TrafficShareWithVisits } from "app/components/React/Table/cells";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import { StyledCoreWebsiteCell } from "app/pages/segments/analysis/StyledComponents";
import { getOverTimeChartAbsNumsConfigCompare } from "app/pages/website-analysis/incoming-traffic/commonOverTime";
import TrafficOverTimeChartNoData from "app/pages/website-analysis/TrafficOverTimeChartNoData";
import { getTableSelectedItems } from "app/reducers/_reducers/tableSelection.reducer";
import CountryService from "app/services/CountryService";
import DurationService from "app/services/DurationService";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import Chart from "pro-features/components/Chart/src/Chart";
import { ETableSelectionNewGroupDropdownMode } from "pro-features/components/TableSelection/src/TableSelectionNewGroupDropdown";
import { TrafficShareWithTooltip } from "pro-features/components/TrafficShare/src/TrafficShareWithTooltip";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { tableActionsCreator } from "../../../../actions/tableActions";
import { showSuccessToast } from "../../../../actions/toast_actions";
import { SWReactTableWrapperWithSelection } from "../../../../components/React/Table/SWReactTableWrapperWithSelection";
import { getToastItemComponent } from "../../../../components/React/Toast/ToastItem";
import { CHART_COLORS } from "../../../../constants/ChartColors";
import { ServerUrl } from "../../../../exporters/HighchartExport";
import { changeFilter, i18nCategoryFilter, smallNumbersPercentageFilter } from "../../../../filters/ngFilters";
import { IncomingReferralsAdvancedFilterService } from "../../../../services/AdvancedFilterService/IncomingReferralsAdvancedFilter";
import { DefaultFetchService } from "../../../../services/fetchService";
import { PngExportService } from "../../../../services/PngExportService";
import { allTrackers } from "../../../../services/track/track";
import { DEFAULT_SORT_DIRECTION, DEFAULT_SORT_FIELD } from "../incomingTrafficConsts";
import { IncomingTrafficLineChart, IncomingTrafficLineChartDataTransformer } from "../IncomingTrafficLineChart";
import { IncomingTrafficPieChart, IncomingTrafficPieChartDataTransformer } from "../IncomingTrafficPieChart";
import {
    CloseIconButton,
    EnrichedRowHeaderTrafficShare,
    ReferralsPage,
    ToggleIconButton,
    TrafficOverTime,
    TrafficOverTimeChangeWrapShare,
    TrafficOverTimeChart,
    TrafficOverTimeChartTitle,
    TrafficOverTimeIndex,
    TrafficOverTimeLeft,
    TrafficOverTimeShareWithAbsWrap,
    TrafficOverTimeTitle,
    TrafficOverTimeWebSiteWrapForAbsNums,
} from "../StyledComponents";
import type { IIncomingTrafficPageData } from "../types";

import { IncomingTrafficCompareTableSettings } from "./IncomingTrafficCompareTableSettings";
import { IncomingTrafficCompareTableTop } from "./IncomingTrafficCompareTableTop";

interface IBackendData {
    AllCategories: Array<{
        Count: number;
        Name: string;
        Value: number;
    }>;
    TopCategories: Array<{
        Count: number;
        Name: string;
        Value: number;
    }>;
    TotalShare: number;
    TotalUnGroupedCount: number;
    TotalCount: number;
    TotalVisits: number;
    TotalVisitsGlobalList: { [key: string]: number };
    Categories: { [key: string]: Array<{ Name: string; Value: number }> };
    Topics: Array<{ Name: string; Value: number }>;
}

interface IIncomingTrafficCompareProps {
    selectedRows: Array<{ Domain: string }>;
    clearAllSelectedRows: () => void;
    showToast: (href, text, label) => void;
    params: any;
}

const i18n = i18nFilter();

class IncomingTrafficCompare extends React.PureComponent<IIncomingTrafficCompareProps, any> {
    private dropdownRef;
    private swNavigator = proNavigator;
    private swSettings: any;
    private pageSize = 100;
    private pieChartInstance;
    private lineChartInstance;

    constructor(props) {
        super(props);
        this.swSettings = swSettings;
        this.dropdownRef = React.createRef();
        this.state = {
            allCategories: [],
            totalSectionData: null,
            newGroupType: ECategoryType.PARTNERS_LIST,
            tableSelectionMode: ETableSelectionNewGroupDropdownMode.GROUP_LIST,
            lineChartTitle: i18n("analysis.common.trafficsource.referrals.title"),
            lineChartTitleTooltip: i18n("analysis.common.trafficsource.referrals.title.tooltip"),
            pageNumber: 0,
            sort: this.props.params?.orderBy,
        };
        this.changePageCallback = this.changePageCallback.bind(this);
    }

    public async componentDidMount() {
        const { orderBy } = this.props.params;
        if (orderBy && orderBy.includes("EngagementScore")) {
            this.swNavigator.updateParams({ orderBy: null });
        }
        const data = await this.getPageData();
        const { lineChartData } = IncomingTrafficLineChartDataTransformer(data, chosenSites);
        const { pieChartData } = IncomingTrafficPieChartDataTransformer(data, chosenSites);
        this.setState({
            lineChartData,
            pieChartData,
        });
    }

    public onCheckboxChange = ({ field, value }) => {
        this.swNavigator.applyUpdateParams({ [field]: value });
    };

    public render() {
        const downloadExcelPermitted = this.swSettings.current.resources.IsExcelAllowed;
        const {
            referralsCategory: category,
            limits,
            engagementTypeFilter,
            webSource,
            duration,
            key,
        } = this.props?.params ?? {};
        const durationObject = DurationService.getDurationData(duration, null, null, null);
        const { from, to, isWindow, isCustom } = durationObject.raw;
        const categoryName = category ? decodeURIComponent(category).split(",") : null;
        const { lineChartTitle, lineChartTitleTooltip } = this.state;
        const hasCustomCategoriesPermission = categoryService.hasCustomCategoriesPermission();
        const selectedRows = this.props.selectedRows;
        const pageSize = this.pageSize;
        const pageNumber = this.state.pageNumber;
        const durationDiff = DurationService.getDiffSymbol(from, to);
        const showTrafficOverTimeChartNoData = ["28d", "1m"].includes(durationDiff);
        return (
            <ReferralsPage className="sharedTooltip">
                {this.state.lineChartData ? (
                    <div className="row-fluid is-compare">
                        <IncomingTrafficPieChart
                            afterRender={this.afterPieChartRender}
                            onExport={this.onPieChartExport}
                            data={this.state.pieChartData}
                            webSource={webSource}
                            to={to}
                            from={from}
                        />
                        <IncomingTrafficLineChart
                            afterRender={this.afterLineChartRender}
                            onExport={this.onLineChartExport}
                            isDaily={isWindow}
                            data={this.state.lineChartData}
                            webSource={webSource}
                            to={to}
                            from={from}
                            title={lineChartTitle}
                            titleTooltip={lineChartTitleTooltip}
                            is28d={duration === "28d"}
                        />
                    </div>
                ) : null}
                <SWReactTableWrapperWithSelection
                    changePageCallback={this.changePageCallback}
                    tableSelectionKey="IncomingTrafficCompare"
                    tableSelectionProperty="Domain"
                    maxSelectedRows={MAX_DOMAINS_IN_CATEGORY}
                    cleanOnUnMount={true}
                    serverApi={this.getApiEndpoint()}
                    initialFilters={this.getInitialFilters()}
                    tableColumns={this.getColumns(hasCustomCategoriesPermission, to)}
                    transformData={this.transformData}
                    getDataCallback={this.onGetData}
                    dataParamsAdapter={this.dataParamsAdapter}
                    tableOptions={{
                        metric: "MarketingWorkspaceWebsiteGroupTable",
                        tableSelectionTrackingParam: "Domain",
                        trackName: "Traffic Sources",
                        get enrichedRowComponentClass() {
                            return `DropdownContent-container ${
                                selectedRows && selectedRows.length > 0 ? "selected" : ""
                            }`;
                        },
                        EnrichedRowComponent: (props) => {
                            if (typeof props?.row === "undefined") {
                                return null;
                            }
                            const {
                                index,
                                Favicon,
                                Domain,
                                trafficDistribution,
                                SiteOriginsPerMonth,
                                TotalVisitsAndSharePerMonth = [],
                                TotalShare,
                            } = props.row;
                            const keys = key.split(",");
                            const clickOutsideXButton = (e) => {
                                allTrackers.trackEvent("Open", "Click", "Traffic Over Time/Collapsed");
                                document.body.click();
                            };
                            const colors = CHART_COLORS.compareMainColors.slice();
                            const categories = SiteOriginsPerMonth.map((x) => new Date(x.Key).getTime());
                            const data = keys.reduce((result, site) => {
                                const siteData = [];
                                // to keep date in order we transform to array
                                const totalVisitsAndSharePerMonthArray = Object.keys(TotalVisitsAndSharePerMonth).sort(
                                    (a, b) => (a > b ? 1 : -1),
                                );
                                for (const date of totalVisitsAndSharePerMonthArray) {
                                    const siteObj = (
                                        TotalVisitsAndSharePerMonth[date] as Array<{
                                            AbsValue: number;
                                            Value: number;
                                            Site: string;
                                        }>
                                    ).find((item) => item.Site === site);
                                    if (siteObj) {
                                        siteData.push({
                                            y: siteObj.AbsValue,
                                            share: siteObj.Value,
                                        });
                                    }
                                }
                                result.push({ name: site, data: siteData });
                                return result;
                            }, []);
                            return (
                                <div>
                                    <TrafficOverTime>
                                        <TrafficOverTimeLeft>
                                            <div onClick={clickOutsideXButton}>
                                                <ToggleIconButton
                                                    iconName="chev-down"
                                                    type="flat"
                                                />
                                            </div>
                                            <TrafficOverTimeIndex>
                                                {pageNumber * pageSize + index + 1}
                                            </TrafficOverTimeIndex>
                                            <TrafficOverTimeWebSiteWrapForAbsNums>
                                                <StyledCoreWebsiteCell
                                                    icon={Favicon}
                                                    domain={Domain}
                                                    externalLink={`http://${Domain}`}
                                                    trackExternalLink={() =>
                                                        allTrackers.trackEvent(
                                                            "external link",
                                                            "click",
                                                            `Conversion Category Overview`,
                                                        )
                                                    }
                                                />
                                            </TrafficOverTimeWebSiteWrapForAbsNums>
                                            <TrafficOverTimeShareWithAbsWrap>
                                                <TrafficOverTimeTitle>
                                                    {i18n(
                                                        "analysis.source.search.all.table.columns.totalShareCompare.title",
                                                    )}
                                                </TrafficOverTimeTitle>
                                                <EnrichedRowHeaderTrafficShare>
                                                    <TrafficShareWithVisits
                                                        {...props.row}
                                                        value={TotalShare}
                                                        row={props.row}
                                                        layout="row"
                                                        applyAbbrNumberFilter={true}
                                                    />
                                                </EnrichedRowHeaderTrafficShare>
                                            </TrafficOverTimeShareWithAbsWrap>
                                            <TrafficOverTimeChangeWrapShare>
                                                <TrafficOverTimeTitle>
                                                    {i18n(
                                                        "analysis.source.search.all.table.columns.shareCompare.title",
                                                    )}
                                                </TrafficOverTimeTitle>
                                                <TrafficShareWithTooltip
                                                    data={trafficDistribution}
                                                    title={i18n("incomingtraffic.competitivetrafficshare.tooltip")}
                                                />
                                            </TrafficOverTimeChangeWrapShare>
                                        </TrafficOverTimeLeft>
                                        <CloseIconButton
                                            type="flat"
                                            onClick={clickOutsideXButton}
                                            iconName="clear"
                                            placement="left"
                                        />
                                    </TrafficOverTime>
                                    {!showTrafficOverTimeChartNoData ? (
                                        <TrafficOverTimeChart>
                                            <TrafficOverTimeChartTitle>
                                                <BoxTitle
                                                    tooltip={i18n(
                                                        "incomingtraffic.overtime.compare.chart.title.tooltip",
                                                    )}
                                                >
                                                    {i18n("incomingtraffic.overtime.compare.chart.title")}
                                                </BoxTitle>
                                            </TrafficOverTimeChartTitle>
                                            <Chart
                                                type="column"
                                                config={getOverTimeChartAbsNumsConfigCompare({
                                                    type: "column",
                                                    colors,
                                                    categories,
                                                })}
                                                data={data}
                                                domProps={{ style: { height: "250px" } }}
                                            />
                                        </TrafficOverTimeChart>
                                    ) : (
                                        <TrafficOverTimeChartNoData />
                                    )}
                                </div>
                            );
                        },
                        get enrichedRowComponentHeight() {
                            return 580;
                        },
                        shouldApplyEnrichedRowHeightToCell: false,
                        shouldEnrichRow: (props, index, e) => {
                            const openEnrich = e?.currentTarget?.childNodes[0]?.className.includes("enrich");
                            if (openEnrich) {
                                allTrackers.trackEvent("Open", "Click", "Traffic Over Time/Expand");
                            }
                            return openEnrich;
                        },
                        onEnrichedRowClick: (isOpen, rowIndex, row) => {
                            // console.log('onEnrichedRowClick');
                        },
                        customTableClass: "incoming-traffic-table",
                    }}
                    recordsField="records"
                    totalRecordsField="TotalCount"
                    onSort={this.onSort}
                    pageIndent={1}
                >
                    {(topComponentProps) => (
                        <IncomingTrafficCompareTableTop
                            {...topComponentProps}
                            selectedLimits={limits}
                            selectedCategoryId={category}
                            selectedCategory={categoryName}
                            allCategories={this.state.allCategories}
                            totalSectionData={this.state.totalSectionData}
                            referralsTrafficShare={this.state.referralsTrafficShare}
                            excelLink={this.getExcel()}
                            downloadExcelPermitted={downloadExcelPermitted}
                            topics={this.state.topics}
                            categoriesData={this.state.categoriesData}
                            domainMetaData={this.state.domainMetaData}
                            onCheckboxChange={this.onCheckboxChange}
                            href={this.swNavigator.href.bind(this.swNavigator)}
                            current={this.swNavigator.current.bind(this.swNavigator)}
                            updateParams={this.swNavigator.updateParams.bind(this.swNavigator)}
                            params={this.props.params}
                            convertLimitsToApiParam={this.convertLimitsToApiParam}
                            selectedRows={this.props.selectedRows}
                            clearAllSelectedRows={this.props.clearAllSelectedRows}
                            showToast={this.props.showToast}
                        />
                    )}
                </SWReactTableWrapperWithSelection>
            </ReferralsPage>
        );
    }

    private changePageCallback(page) {
        this.setState({ pageNumber: page });
    }

    private afterLineChartRender = (chart) => {
        this.lineChartInstance = chart;
        return {};
    };

    private afterPieChartRender = (chart) => {
        this.pieChartInstance = chart;
        return {};
    };

    // We wrap this in a Promise because the DownloadMenuButton expects a promise for PNG downloads
    private onLineChartExport = (): Promise<void> => {
        return new Promise((resolve) => {
            allTrackers.trackEvent(`Download`, `submit-ok`, `Referral Visits/PNG`);
            if (this.lineChartInstance) {
                const { lineChartTitle } = this.state;
                let trimmedDateTitle =
                    lineChartTitle.indexOf("from") != -1
                        ? lineChartTitle.substr(0, lineChartTitle.indexOf("from") - 1)
                        : lineChartTitle;
                trimmedDateTitle =
                    lineChartTitle.indexOf("Last") != -1
                        ? lineChartTitle.substr(0, lineChartTitle.indexOf("Last") - 1)
                        : trimmedDateTitle;
                PngExportService.getInstance().export(this.lineChartInstance, trimmedDateTitle);
                // using setTimeout to trigger the png buttons loading state which will trigger a loading animation in the button.
                setTimeout(() => resolve(), 3000);
                return;
            }
            resolve();
        });
    };

    private dataParamsAdapter = (params) => {
        const transformedParams = { ...params };
        if (transformedParams.filter) {
            delete transformedParams.filter;
        }
        if (transformedParams.sort) {
            transformedParams.orderBy = `${transformedParams.sort} ${transformedParams.asc ? "asc" : "desc"}`;
            delete transformedParams.sort;
            delete transformedParams.asc;
        }
        if (transformedParams.referralsCategory) {
            transformedParams.Category = transformedParams.referralsCategory;
            delete transformedParams.referralsCategory;
        }
        // convert limits param to actual api param
        if (transformedParams.limits) {
            transformedParams.limits = this.convertLimitsToApiParam(transformedParams.limits);
        }
        return transformedParams;
    };

    private onPieChartExport = (): Promise<void> => {
        return new Promise((resolve) => {
            allTrackers.trackEvent(`Download`, `submit-ok`, `Traffic Share/PNG`);
            if (this.pieChartInstance) {
                const chartTitle = i18n("analysis.compare.trafficsource.overview.share");
                const exportOptions = {
                    exporting: {
                        url: ServerUrl,
                    },
                    filename: chartTitle.replace(/[,`]/g, " "),
                };
                const chartOptions = {
                    title: {
                        text: chartTitle,
                        style: {
                            fontSize: "14px",
                            fontFamily: '"Roboto", sans-serif',
                        },
                    },
                    plotOptions: {
                        pie: {
                            dataLabels: {
                                enabled: false,
                            },
                        },
                    },
                };
                this.pieChartInstance.exportChart(exportOptions, chartOptions);
                // using setTimeout to trigger the png buttons loading state which will trigger a loading animation in the button.
                setTimeout(() => resolve(), 3000);
                return;
            }
            resolve();
        });
    };
    public getColumns = (useSelection, to) => {
        const { duration, webSource, country } = this.props.params;
        const isUSState = CountryService.isUSState(country);
        let sortedColumn;
        if (this.state.sort) {
            const [field, sortDirection] = this.state.sort.split(" ");
            sortedColumn = {
                field,
                sortDirection,
            };
        }
        const columns = IncomingTrafficCompareTableSettings.getColumns(
            sortedColumn,
            useSelection,
            webSource,
            duration === "28d",
            !to.isBefore("2018-10-01"),
            undefined,
            isUSState,
        );
        return columns;
    };

    public convertLimitsToApiParam = (limits) => {
        const filters = IncomingReferralsAdvancedFilterService.getAllFilters();
        return filters.find((filter) => filter.id === limits).api;
    };

    public getInitialFilters = () => {
        const params = { ...this.props.params };
        // orderBy
        if (!params.orderBy) {
            params.orderBy = `${DEFAULT_SORT_FIELD} ${DEFAULT_SORT_DIRECTION}`;
        }
        const apiParams = apiHelper.transformParamsForAPI(params);
        return apiParams;
    };

    public getApiEndpoint = () => {
        const { webSource } = this.props.params;
        switch (webSource) {
            case "MobileWeb":
                return "/api/websiteanalysis/GetTrafficSourcesMobileWebReferralsTable";
            case "Total":
                return "/api/websiteanalysis/GetTrafficSourcesTotalReferralsTable";
            case "Desktop":
                return "/api/websiteanalysis/GetTrafficSourcesReferralsTable";
        }
    };

    public transformData = (data) => {
        const params = this.props.params;
        const keys = params.key.split(",");
        return {
            ...data,
            records: data.Records.map((record) => {
                return {
                    ...record,
                    TotalSharePerMonth: record.TotalSharePerMonth ? record.TotalSharePerMonth : [],
                    SiteOriginsPerMonth: record.SiteOriginsPerMonth ? record.SiteOriginsPerMonth : [],
                    url: this.swNavigator.href("digitalsuite_website_websiteperformance", {
                        ...params,
                        key: record.Domain,
                    }),
                    EngagementScores: record.EngagementScores
                        ? Object.entries(record.EngagementScores).map(([key, val]) => ({
                              name: key,
                              score: val,
                          }))
                        : [],
                    TotalShare: record.FilteredShare && record.FilteredShare,
                    trafficDistribution: record.SiteOrigins
                        ? Object.entries(record.SiteOrigins).map(([site, value], index) => {
                              const colorIndex = keys.indexOf(site);
                              return {
                                  color: colorsPalettes.carbon[0],
                                  backgroundColor: CHART_COLORS.main[colorIndex],
                                  width: value,
                                  // @ts-expect-error FIXME: Argument of type 'unknown' is not assignable to parameter of type 'string'
                                  text: smallNumbersPercentageFilter()(value, 1),
                                  name: site,
                              };
                          })
                        : [],
                    Children: Array.isArray(record.Children)
                        ? record.Children.map((child) => {
                              return {
                                  ...child,
                                  url: this.swNavigator.href("digitalsuite_website_websiteperformance", {
                                      ...params,
                                      key: child.Domain,
                                  }),
                                  trafficDistribution: child.SiteOrigins
                                      ? Object.entries(child.SiteOrigins).map(([site, value], index) => {
                                            const colorIndex = keys.indexOf(site);
                                            return {
                                                color: colorsPalettes.carbon[0],
                                                backgroundColor: CHART_COLORS.main[colorIndex],
                                                width: value,
                                                // @ts-expect-error FIXME: Argument of type 'unknown' is not assignable to parameter of type 'string'
                                                text: smallNumbersPercentageFilter()(value, 1),
                                                name: site,
                                            };
                                        })
                                      : [],
                              };
                          })
                        : null,
                };
            }),
            TotalVisitsGlobalList: Object.entries<number>(data.TotalVisitsGlobalList).map(([domain, percentage]) => {
                return {
                    color: chosenSites.getSiteColor(domain),
                    name: domain,
                    percentage,
                    valueText: changeFilter()(percentage),
                    width: percentage * 100,
                };
            }),
        };
    };

    public onGetData = (data: IBackendData) => {
        // This code uses legacy chosenItems service to populate the categories table
        const temp = {
            categories: {
                CategoriesList: [],
            },
        };
        chosenSites.registerLists(
            temp,
            [
                {
                    name: "Categories",
                    topList: data.TopCategories,
                    func(obj, category) {
                        if (category.Name.toLowerCase() !== "others" && category.Name.toLowerCase() !== "other") {
                            obj.Id = category.Name.replace("/", "~");
                        }
                        return obj;
                    },
                },
            ],
            "categories",
            data,
        );
        //

        this.setState({
            allCategories: data.AllCategories,
            topCategories: data.TopCategories,
            totalSectionData: {
                totalShare: data.TotalShare > 0.99 ? 1 : data.TotalShare,
                totalUnGroupedCount: data.TotalCount,
                totalVisits: data.TotalVisits,
            },
            referralsTrafficShare: data.TotalVisitsGlobalList,
            topics: data.Topics,
            categoriesData: temp.categories.CategoriesList.map((cat) => {
                return {
                    category: i18nCategoryFilter()(cat.name),
                    categoryApi: cat.Id,
                    ...Object.entries(data.Categories).reduce((acc, [domain, domainCategories]) => {
                        const { value } = cat.sites.find((site) => site.name === domain);
                        return {
                            ...acc,
                            [domain]: [value],
                        };
                    }, {}),
                };
            }),
            domainMetaData: chosenSites.map((site) => {
                return {
                    icon: chosenSites.listInfo[site].icon,
                    name: site,
                    color: chosenSites.getSiteColor(site),
                };
            }),
        });
    };
    public getExcel = () => {
        const params = this.dataParamsAdapter(this.getInitialFilters());
        const queryStringParams = queryString.stringify(params);
        return `/export/analysis/GetTrafficSourcesReferralsTsv?${queryStringParams}`;
    };

    public showSuccessToast = (name, isNewGroup) => {
        this.props.clearAllSelectedRows();
        this.setState({
            tableSelectionMode: ETableSelectionNewGroupDropdownMode.GROUP_LIST,
            newGroupErrorMessage: null,
            newGroupError: false,
        });
        this.dropdownRef.current.close();
        const text = isNewGroup
            ? i18n("table.selection.websites.groupcreated")
            : i18n("table.selection.websites.groupupdated");
        this.showGroupLinkToast(name, text, i18n("table.selection.websites.seegroup"));
    };

    public showGroupLinkToast = (name, text, label) => {
        const params = this.props.params;
        const linkToGroup = this.swNavigator.href("digitalsuite_marketresearch_webmarketanalysis_overview", {
            ...params,
            category: `*${name}`,
        });
        this.dropdownRef.current.close();
        this.props.showToast(linkToGroup, text, label);
    };

    public onSort = ({ field, sortDirection }) => {
        this.swNavigator.applyUpdateParams({
            orderBy: `${field} ${sortDirection}`,
        });
        this.setState({
            sort: `${field} ${sortDirection}`,
        });
    };

    public setStateAsync(newState) {
        return new Promise<void>((resolve, reject) => {
            this.setState(newState, resolve);
        });
    }

    public onListTypeSelect = (typeId) => {
        this.setState({ newGroupType: typeId });
    };

    private getPageData = (): Promise<IIncomingTrafficPageData> => {
        const params = { ...this.props.params };
        Object.keys(params).forEach((key) => params[key] === undefined && delete params[key]);
        if (params.limits) {
            delete params.limits;
        }
        const apiParams = apiHelper.transformParamsForAPI(params);
        let endpoint = "";
        switch (params.webSource) {
            case "MobileWeb":
                endpoint = "/api/websiteanalysis/GetTrafficSourcesMobileWebReferrals";
                break;
            case "Total":
                endpoint = "/api/websiteanalysis/GetTrafficSourcesTotalReferrals";
                break;
            case "Desktop":
                endpoint = "/api/websiteanalysis/GetTrafficSourcesReferrals";
                break;
        }

        return DefaultFetchService.getInstance().get(endpoint, apiParams);
    };
}

const TABLE_SELECTION_KEY = "IncomingTrafficCompare";

const mapStateToProps = (state) => {
    return {
        selectedRows: getTableSelectedItems(TABLE_SELECTION_KEY)(state),
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        clearAllSelectedRows: () => {
            dispatch(tableActionsCreator(TABLE_SELECTION_KEY, "Domain").clearAllSelectedRows()); // todo
        },
        showToast: (href, text, label) => {
            dispatch(
                showSuccessToast(
                    getToastItemComponent({
                        text,
                        linkText: label,
                        href,
                        onClick: () =>
                            allTrackers.trackEvent(
                                "add to Custom Category",
                                "click",
                                "internal link/websites.overview",
                            ),
                    }),
                ),
            );
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps, null)(IncomingTrafficCompare);
